import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, History, Lock, ShieldCheck, Undo2, Unlock, XCircle } from 'lucide-react';
import { Drawer, QueryState, StatusPill, formatDateTime } from '@/components/live';
import { RecordLockBar } from '@/components/RecordLockBar';
import { RecordHistory } from '@/components/RecordHistory';
import { callRpc } from '@/api/resources';
import { isSupabaseConfigured } from '@/lib/supabase';

export interface RecordRef {
  /** The record's table, e.g. 'ops.accidents'. */
  table: string;
  id: string;
  /** Further ids whose changes belong to this record's history (a plot's boundary ...). */
  historyIds?: Array<string | null | undefined>;
  /** Query keys to refresh after a lock or unlock. */
  invalidate?: string[];
}

type Tab = 'details' | 'approval' | 'history' | 'extra' | `more-${number}`;

/**
 * Every form and detail view of a saved record in the same three tabs (owner, 2026-09-22: "Add
 * approval history tab in all form and detail views", "Change history is on all detail views.
 * Tab type structure in all forms and detail views"):
 *   Details        - the form or the view itself;
 *   Approval & lock - whether it is locked, lock / unlock, and every request, decision, lock and
 *                     unlock in order;
 *   Change history - what changed, when and by whom, with comments.
 * A new record (no id yet) shows the form alone.
 */
/**
 * A form's own tabs, shown in the drawer's single tab row in place of "Details" (owner,
 * 2026-09-27: "can we have single tab row? in all places?"): Member | Address | Contact | Approval &
 * lock | Change history, rather than a row of form tabs under a row of record tabs.
 */
export interface DrawerSections {
  items: Array<{ id: string; label: ReactNode }>;
  active: string;
  onChange: (id: string) => void;
}

export function RecordDrawer({ record, children, footer, extraTab, moreTabs, sections, ...drawer }: {
  record?: RecordRef | null;
  title: string;
  subtitle?: string;
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
  /** One more section for record types that have one - a plot's satellite monitoring, say. */
  extraTab?: { label: string; icon?: ReactNode; content: ReactNode };
  /** Further sections after it - a plot's consents beside its satellite monitoring. */
  moreTabs?: Array<{ label: string; icon?: ReactNode; content: ReactNode }>;
  sections?: DrawerSections;
}) {
  const [tab, setTab] = useState<Tab>('details');
  if (!record) {
    return (
      <Drawer {...drawer} footer={footer}>
        {sections && <RecordTabs onChange={setTab} sections={sections} tab="details" withRecord={false} />}
        {children}
      </Drawer>
    );
  }
  return (
    <Drawer {...drawer} footer={tab === 'details' ? footer : undefined}>
      <RecordTabs extra={extraTab ? { label: extraTab.label, icon: extraTab.icon } : undefined} more={moreTabs?.map((entry) => ({ label: entry.label, icon: entry.icon }))}
                  onChange={setTab} sections={sections} tab={tab} />
      {/* The panel the tabs above control. Without it a reader hears "tab 2 of 4" and has nothing
          to move to; one panel, relabelled by whichever tab is current. */}
      <div aria-labelledby={tab === 'details' && sections ? `record-tab-section-${sections.active}` : `record-tab-${tab}`} id="record-tab-panel" role="tabpanel">
        {tab === 'details' && children}
        {tab === 'approval' && <ApprovalPanel record={record} />}
        {tab === 'history' && (
          <RecordHistory commentOn={{ table: record.table, id: record.id }} entityIds={[record.id, ...(record.historyIds ?? [])]} />
        )}
        {tab === 'extra' && extraTab?.content}
        {tab.startsWith('more-') && moreTabs?.[Number(tab.slice(5))]?.content}
      </div>
    </Drawer>
  );
}

export function RecordTabs({ tab, onChange, extra, more, sections, withRecord = true }: {
  tab: Tab; onChange: (tab: Tab) => void; extra?: { label: string; icon?: ReactNode };
  more?: Array<{ label: string; icon?: ReactNode }>;
  sections?: DrawerSections;
  /** A record not saved yet has no approvals or history: only the form's own tabs. */
  withRecord?: boolean;
}) {
  const recordTabs: Array<{ id: Tab; label: string; icon: ReactNode }> = withRecord ? [
    { id: 'approval', label: 'Approval & lock', icon: <ShieldCheck size={14} /> },
    { id: 'history', label: 'Change history', icon: <History size={14} /> },
    ...(extra ? [{ id: 'extra' as const, label: extra.label, icon: extra.icon ?? null }] : []),
    ...(more ?? []).map((entry, index) => ({ id: `more-${index}` as Tab, label: entry.label, icon: entry.icon ?? null })),
  ] : [];
  return (
    <div aria-label="Record sections" className="record-tabs" role="tablist">
      {sections
        ? sections.items.map((entry) => {
            const active = tab === 'details' && sections.active === entry.id;
            return (
              <button aria-controls="record-tab-panel" aria-selected={active} className={active ? 'active' : undefined}
                      id={`record-tab-section-${entry.id}`} key={entry.id}
                      onClick={() => { onChange('details'); sections.onChange(entry.id); }} role="tab" type="button">
                {entry.label}
              </button>
            );
          })
        : withRecord && (
          <button aria-controls="record-tab-panel" aria-selected={tab === 'details'} className={tab === 'details' ? 'active' : undefined}
                  id="record-tab-details" onClick={() => onChange('details')} role="tab" type="button">Details</button>
        )}
      {recordTabs.map((entry) => (
        <button aria-controls="record-tab-panel" aria-selected={tab === entry.id}
                className={tab === entry.id ? 'active' : undefined} id={`record-tab-${entry.id}`} key={entry.id}
                onClick={() => onChange(entry.id)} role="tab" type="button">
          {entry.icon}{entry.label}
        </button>
      ))}
    </div>
  );
}

interface ApprovalEvent { event_at: string; event: string; actor_name: string | null; remarks: string | null; request_status: string | null }

const EVENT_TEXT: Record<string, { label: string; icon: ReactNode }> = {
  submitted: { label: 'Sent for approval', icon: <ShieldCheck size={15} /> },
  approved: { label: 'Approved', icon: <CheckCircle2 size={15} /> },
  approve: { label: 'Approved', icon: <CheckCircle2 size={15} /> },
  rejected: { label: 'Rejected', icon: <XCircle size={15} /> },
  reject: { label: 'Rejected', icon: <XCircle size={15} /> },
  returned: { label: 'Returned for changes', icon: <Undo2 size={15} /> },
  return: { label: 'Returned for changes', icon: <Undo2 size={15} /> },
  lock: { label: 'Locked', icon: <Lock size={15} /> },
  unlock: { label: 'Unlocked', icon: <Unlock size={15} /> },
};

/** Lock status and controls, then the record's approval trail. */
export function ApprovalPanel({ record }: { record: RecordRef }) {
  const history = useQuery({
    queryKey: ['record-approval-history', record.table, record.id],
    queryFn: () => callRpc<ApprovalEvent[]>('fn_record_approval_history', { p_entity_table: record.table, p_entity_id: record.id }),
    enabled: isSupabaseConfigured,
  });
  return (
    <div className="approval-panel">
      <RecordLockBar entityId={record.id} entityTable={record.table} invalidate={[...(record.invalidate ?? []), 'record-approval-history']} />
      <h3 className="approval-panel-title">Approval history</h3>
      <QueryState empty="No approvals yet. Changes by an admin do not need one; anyone else's change is sent to a higher role." query={history}>
        <ol className="approval-trail">
          {(history.data ?? []).map((entry, index) => {
            const known = EVENT_TEXT[entry.event] ?? { label: entry.event.replace(/_/g, ' '), icon: <ShieldCheck size={15} /> };
            return (
              <li className={`approval-step is-${entry.event}`} key={`${entry.event_at}-${index}`}>
                <span className="approval-step-icon">{known.icon}</span>
                <span className="approval-step-text">
                  <strong>{known.label}</strong>
                  <small className="muted">{[formatDateTime(entry.event_at), entry.actor_name ? `by ${entry.actor_name}` : null].filter(Boolean).join(' · ')}</small>
                  {entry.remarks && <small>{entry.remarks}</small>}
                </span>
                {entry.event === 'submitted' && entry.request_status && <StatusPill value={entry.request_status} />}
              </li>
            );
          })}
        </ol>
      </QueryState>
    </div>
  );
}
